import { Component, ErrorInfo, ReactNode } from "react";

const RELOAD_KEY = "vps-chunk-reload-ts";
const COOLDOWN_MS = 10000;

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * Catches render errors. If a lazily-imported route chunk fails to load
 * (typically a stale chunk after a redeploy), it reloads the page once —
 * guarded by a cooldown so it can never loop.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const msg = error?.message || "";
    const isChunkError =
      /dynamically imported module|Loading chunk|Importing a module script failed|Failed to fetch/i.test(msg);
    if (isChunkError) {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      } catch {
        /* ignore */
      }
      const now = Date.now();
      if (now - last > COOLDOWN_MS) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(now));
        } catch {
          /* ignore */
        }
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4 text-sm">Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.reload()}
              className="text-primary font-semibold underline underline-offset-4"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
